import { signOutApi } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { redirectToLoginPage } from '@dealstack/frontend/shared/redirecting';
import { SessionEndReason } from '@dealstack/shared/lib/common/session/sessionContract';
import {
	beginIntentionalSignOut,
	resetIntentionalSignOut,
} from '@dealstack/frontend/shared/mechanism/session/intentionalSignOut';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

export const SIGN_OUT_TIMEOUT_MS = 5000;

/**
 * Asks the server to end this session, declared first so a request that loses the race
 * cannot redirect as an unexpected end. Resolves false only when the server gave no answer
 * in time: sign-out tolerates an expired or absent token, so a refusal still means the
 * session is unusable.
 */
const endSessionIntentionally = async (): Promise<boolean> => {
	beginIntentionalSignOut();
	try {
		return await Promise.race([
			signOutApi().then(() => true),
			new Promise<boolean>((resolve) =>
				setTimeout(() => resolve(false), SIGN_OUT_TIMEOUT_MS)
			),
		]);
	} catch {
		return true;
	}
};

/**
 * Signs this device out and replaces the document with the sign-in page. Resolves false,
 * having said so, when the server gave no answer in time.
 */
export const useSignOut = () => {
	const tCommonUi = useTranslationCommonUi();

	return async (): Promise<boolean> => {
		// A hung request may never have revoked the session, and the login route lands a live
		// session straight back in the app - so say so rather than pretend the user left.
		if (!(await endSessionIntentionally())) {
			resetIntentionalSignOut();
			showToastV2({ state: 'error', message: tCommonUi('sign_out.error.timeout') });
			return false;
		}

		// No logout() dispatch: it would render an empty account until the navigation lands.
		// SIGNED_OUT, so a request that 401s on the way out cannot report an unexpected end.
		redirectToLoginPage(undefined, SessionEndReason.SIGNED_OUT);
		return true;
	};
};
